import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type {
  Usuario,
  CrearUsuarioConRolRequest,
  ActualizarUsuarioRequest,
  ResetearPasswordRequest,
  PaginatedResult,
} from '@suminuta/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// ============================================
// QUERIES
// ============================================

/**
 * Hook para obtener lista paginada de usuarios
 * @param params - Parámetros de paginación y filtros
 */
export const useUsuarios = (params?: {
  page?: number;
  limit?: number;
  rol?: string;
  search?: string;
  activo?: boolean;
}) => {
  return useQuery({
    queryKey: ['usuarios', params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.rol) queryParams.append('rol', params.rol);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.activo !== undefined)
        queryParams.append('activo', params.activo.toString());

      const { data } = await axios.get<{
        success: boolean;
        data: Omit<Usuario, 'password_hash'>[];
        pagination: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      }>(`${API_URL}/usuarios?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      return {
        data: data.data,
        pagination: data.pagination,
      } as PaginatedResult<Omit<Usuario, 'password_hash'>>;
    },
  });
};

/**
 * Hook para obtener un usuario por ID
 * @param id - ID del usuario
 */
export const useUsuario = (id: number) => {
  return useQuery({
    queryKey: ['usuario', id],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: Omit<Usuario, 'password_hash'>;
      }>(`${API_URL}/usuarios/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      return data.data;
    },
    enabled: !!id,
  });
};

/**
 * Hook para obtener estadísticas de usuarios
 */
export const useEstadisticasUsuarios = () => {
  return useQuery({
    queryKey: ['usuarios', 'estadisticas'],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: {
          total: number;
          activos: number;
          porRol: Record<string, number>;
          registrosRecientes: Omit<Usuario, 'password_hash'>[];
        };
      }>(`${API_URL}/usuarios/estadisticas`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      return data.data;
    },
  });
};

// ============================================
// MUTATIONS
// ============================================

/**
 * Hook para crear un nuevo usuario
 */
export const useCrearUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (usuario: CrearUsuarioConRolRequest) => {
      try {
        const { data } = await axios.post<{
          success: boolean;
          message: string;
          data: Omit<Usuario, 'password_hash'>;
        }>(`${API_URL}/usuarios`, usuario, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        return data;
      } catch (error) {
        console.error('Error al crear usuario:', error);
        if (axios.isAxiosError(error) && error.response) {
          console.error('Detalles del error:', error.response.data);
        }
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({
        queryKey: ['usuarios', 'estadisticas'],
      });
    },
  });
};

/**
 * Hook para actualizar un usuario
 */
export const useActualizarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ActualizarUsuarioRequest;
    }) => {
      const response = await axios.put<{
        success: boolean;
        message: string;
        data: Omit<Usuario, 'password_hash'>;
      }>(`${API_URL}/usuarios/${id}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({ queryKey: ['usuario', variables.id] });
      queryClient.invalidateQueries({
        queryKey: ['usuarios', 'estadisticas'],
      });
    },
  });
};

/**
 * Hook para eliminar un usuario (soft delete)
 */
export const useEliminarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await axios.delete<{
        success: boolean;
        message: string;
      }>(`${API_URL}/usuarios/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      return data;
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({
        queryKey: ['usuarios', 'estadisticas'],
      });
    },
  });
};

/**
 * Hook para resetear la contraseña de un usuario
 */
export const useResetearPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      usuario_id,
      nueva_password,
    }: ResetearPasswordRequest) => {
      const { data } = await axios.post<{
        success: boolean;
        message: string;
      }>(
        `${API_URL}/usuarios/${usuario_id}/reset-password`,
        { nueva_password },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );
      return data;
    },
    onSuccess: () => {
      // Invalidar queries de usuarios
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });
};
